// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { ErrorBoundary } from '@ta/common.error-handling';
// eslint-disable-next-line no-restricted-imports
import { actions, getStore } from '@ta/common.state';
import { Overlay } from '@ta/overlays.pieces';
import { ToastItem } from '@ta/core-ui.toast';
import { type ToastType, type LaunchToast, type RemoveToast } from './types';
import styles from './Toaster.less';

type ExternalProps = {|
  /** Children of the Toaster that are responsible for launching the toast */
  children: (launchToast: LaunchToast, removeToast: RemoveToast) => React.Node,
  /** Optional handler for when a toast is dismissed */
  onToastRemoved?: () => void,
|};

type Props = {|
  ...ExternalProps,
  /** All currently active toasts, set via connect */
  toasts: string[],
|};

type ToastTypeWithWrapper = {|
  toast: ToastType,
  wrapper: ?(React.Node) => React.Node,
|};

type State = {|
  /* array of toast notifications with their optional wrapping functions */
  toasts: ToastTypeWithWrapper[],
|};

const TOAST_DEFAULT_TIMEOUT = 4000;

/**
 * Component for launching customizable toasts. Will also serve as a bridge for multi root pages.
 */
class Toaster extends React.Component<Props, State> {
  static defaultProps = {
    onToastRemoved: undefined,
  };
  // keep track of timeouts so they can be cleared during unmount
  // eslint-disable-next-line react/sort-comp
  removalTimeouts: TimeoutID[] = [];

  constructor(props: Props) {
    super(props);
    this.state = {
      toasts: [],
    };
  }

  componentWillUnmount() {
    this.clearTimeouts();
  }

  clearTimeouts = () => {
    this.removalTimeouts.forEach(timeout => clearTimeout(timeout));
    this.removalTimeouts = [];
  };

  /**
   * Method to launch a toast.
   * @param toast - the toast to be displayed
   * @param wrapper - optional rendering function that can wrap the ToastItem in non-renderable components
   */
  launchToast = (toast: ToastType, wrapper?: ?(React.Node) => React.Node): void => {
    const toastId = toast.id
      ? toast.id
      : `toast_${Math.random()
          .toString(36)
          .replace(/[^a-z]+/g, '')}`;

    const toastWithId: ToastType = { id: toastId, ...toast };
    // clear timeouts that are going to clean up old toasts because we'll be clearing out the list imminently
    this.clearTimeouts();
    this.setState({
      // clear out existing toasts. Multiple simultaneous toasts are supported but for now we should clear out existing ones
      toasts: [{ toast: toastWithId, wrapper }],
    });
    this.removalTimeouts.push(
      setTimeout(() => {
        this.removeToast(toastId);
      }, toastWithId.timeout || TOAST_DEFAULT_TIMEOUT),
    );
    getStore().dispatch(actions.overlays.launchExclusiveToast(toastId));
  };

  removeToast = (toastId: string): void => {
    this.setState({
      toasts: this.state.toasts.filter(tst => tst.toast.id !== toastId),
    });
    getStore().dispatch(actions.overlays.removeToast(toastId));
    if (this.props.onToastRemoved) {
      this.props.onToastRemoved();
    }
  };

  render() {
    return (
      <React.Fragment>
        {this.props.children(this.launchToast, this.removeToast)}
        <ErrorBoundary>
          {/* component={null} is needed so that the <TransitionGroup> doesn't render an extra <div>, which would mess with our flex styling. */}
          <TransitionGroup component={null} enter exit>
            {this.state.toasts
              .filter(t => this.props.toasts.includes(t.toast.id))
              .map(toastWithWrapper => {
                const {
                  toast: { id, iconName, node, cta },
                  wrapper,
                } = toastWithWrapper;
                return (
                  <CSSTransition
                    timeout={300}
                    key={id}
                    classNames={{
                      enter: styles.fadeInEnter,
                      enterActive: styles.fadeInEnterActive,
                      enterDone: styles.fadeInEnterDone,
                      exit: styles.fadeOutExit,
                      exitActive: styles.fadeOutExitActive,
                      exitDone: styles.fadeOutExitDone,
                    }}
                  >
                    <Overlay
                      perspective="300px"
                      position={{
                        position: 'fixed',
                        bottom: '0',
                        left: '0',
                        right: '0',
                        height: 'auto',
                      }}
                    >
                      {wrapper ? (
                        wrapper(
                          <ToastItem iconName={(iconName: any)} cta={cta}>
                            {node}
                          </ToastItem>,
                        )
                      ) : (
                        <ToastItem iconName={(iconName: any)} cta={cta}>
                          {node}
                        </ToastItem>
                      )}
                    </Overlay>
                  </CSSTransition>
                );
              })}
          </TransitionGroup>
        </ErrorBoundary>
      </React.Fragment>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  toasts: state.overlays.toasts,
}))(Toaster);
